import {ChangeDetectorRef, Component, DestroyRef, inject, OnInit, ViewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {BehaviorSubject, finalize, of} from 'rxjs';
import {map, take} from 'rxjs/operators';
import * as R from 'ramda';
import {TreeNode, TreeModel, KEYS} from '@ali-hm/angular-tree-component';
import {ConsumerManagerFormComponent} from '../consumer-manager-form/consumer-manager-form.component';
import {GenericItemsTreeComponent} from '../generic-items-tree/generic-items-tree.component';
import {AddConsumerDialogComponent} from '@emm/shared/dialogs/add-consumer-dialog/add-consumer-dialog.component';
// tslint:disable-next-line:max-line-length
import {
    AddConsumerCategoryDialogComponent
} from '@emm/shared/dialogs/add-consumer-category-dialog/add-consumer-category-dialog.component';
import {
    DuplicateConsumerDialogComponent
} from '@emm/shared/dialogs/duplicate-consumer-dialog/duplicate-consumer-dialog.component';
import {
    DeleteConsumerDialogComponent
} from '@emm/shared/dialogs/delete-consumer-dialog/delete-consumer-dialog.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ItemNode, TreeUtil} from '@emm/core/util/angular-tree-component.util';
import {ContextMenuAction} from '@emm/core/models/context-menu-action.model';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {ManagementApiService} from '@emm/management/services/management-api.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {ManagementStoreService} from '@emm/management/services/management-store.service';
import {Consumer} from '@emm/core/models/consumer.model';
import {SearchFilter} from '@emm/core/models/search-filter';
import {UserService} from '@emm/core/services/user.service';
import {API_ERRORS} from "@emm/core/util/api-constants";

@Component({
    selector: 'emm-consumer-manager',
    styleUrls: ['./consumer-manager.component.scss'],
    templateUrl: './consumer-manager.component.html'
})
export class ConsumerManagerComponent implements OnInit {

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    @ViewChild(ConsumerManagerFormComponent) consumerManagerForm: ConsumerManagerFormComponent;
    @ViewChild(GenericItemsTreeComponent) tree: GenericItemsTreeComponent;

    anyLoading$ = this.managementStore.anyLoading$;
    isReadOnly$ = this.authFacade.isReadOnly$;
    isReadOnly = this.userService.hasLoginRole();
    isSuperAdmin = this.userService.hasSuperAdminRole();
    isAdmin = this.userService.hasAdminRole();
    isAuthor = this.userService.hasAuthorRole();
    selectedConsumerInfo = new BehaviorSubject<Consumer>(null);
    disableButtons$ = new BehaviorSubject<boolean>(true);

    fields$ = of([
        {
            key: 'consumerCategoryName',
            type: 'input',
            templateOptions: {
                label: 'Name',
                placeholder: 'Enter Category Name',
                required: false
            }
        }
    ]);

    nodes$: BehaviorSubject<ItemNode[]> = new BehaviorSubject<ItemNode[]>([]);
    nodesCache: ItemNode[] = [];
    selectedNode: TreeNode;
    selectedNode$: BehaviorSubject<TreeNode> = new BehaviorSubject<TreeNode>(null);

    consumerCategories$ = this.managementApiService.getConsumerCategories();

    options = {
        getChildren: (node: TreeNode) =>
            this.getConsumersForCategory(node.data.id),
        actionMapping: {
            mouse: {
                click: (tree: TreeModel, node: TreeNode) => {
                    this.selectConsumer(node);
                }
            },
            keys: {
                [KEYS.ENTER]: (tree, node) => alert(`This is ${node.data.name}`)
            }
        }
    };

    consumerManagerContextMenuActions = [
        {
            // Add
            enabled: () => true,
            click: (node) => this.openCreateConsumerDialog(node),
            visible: (node) => this.isSuperAdmin ? TreeUtil.isRoot(node) : false,
            icon: R.always('add'),
            text: R.always('Add Consumer'),
            tooltip: () => 'Create a new Consumer in this Category'
        },
        {
            // Delete
            enabled: () => true,
            click: (node) => this.openDeleteConsumerDialog(node),
            visible: (node) => this.isSuperAdmin ? node?.data?.consumerType && !node?.data?.locked : false,
            icon: R.always('delete'),
            text: R.always('Delete Consumer'),
            tooltip: () => 'Delete a Consumer in this Category'
        },
        {
            // Duplicate
            enabled: () => true,
            click: (node) => this.duplicateConsumer(node),
            visible: (node) =>  (this.isSuperAdmin || this.isAdmin || this.isAuthor) ? node?.data?.consumerType : false,
            icon: R.always('file_copy'),
            text: R.always('Duplicate Consumer'),
            tooltip: () => 'Duplicate this Consumer in this Category'
        }
    ] as ContextMenuAction<TreeNode>[];

    constructor(
        private authFacade: AuthFacadeService,
        private dialog: MatDialog,
        private managementApiService: ManagementApiService,
        private notificationService: NotificationService,
        private managementStore: ManagementStoreService,
        private userService: UserService,
        private  changeDetector: ChangeDetectorRef
    ) {
    }

    ngOnInit(): void {
        this.getConsumerCategories();
    }

    startProgressBar() {
        this.managementStore.dispatch.startLoading();
    }

    stopProgressBar() {
        this.managementStore.dispatch.stopLoading();
    }

    getConsumerCategories() {
        this.startProgressBar();
        const tempNodes: ItemNode[] = [];
        this.consumerCategories$
            .pipe(
                finalize(() => this.stopProgressBar()),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(cc => {
            cc.forEach(cat => {
                tempNodes.push(
                    {
                        data: {},
                        hasChildren: true,
                        id: cat.id,
                        name: cat.name
                    });

            });

            this.nodes$.next(tempNodes);
            this.nodesCache = tempNodes;
        });
    }

    getConsumersForCategory(id: string): Promise<Consumer[]> {
        return this.managementApiService.getConsumersForCategory(id).toPromise();
    }

    async getConsumer(id: string) {
        this.managementApiService.getConsumer(id).pipe().subscribe(consumer =>
            this.selectedConsumerInfo.next(consumer));
    }

    openCreateConsumerCategoryDialog(): void {
        const dialogRef = this.dialog.open(AddConsumerCategoryDialogComponent, {
            width: '600px',
            data: {}
        });
        dialogRef
            .afterClosed()
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(consumerCategoryToCreate => {
                if (consumerCategoryToCreate !== undefined) {
                    this.startProgressBar();
                    this.managementApiService.createConsumerCategory(consumerCategoryToCreate)
                        .pipe(
                            finalize(() => this.stopProgressBar()),
                            takeUntilDestroyed(this.destroyRef)
                        )
                        .subscribe({
                            next: (createdConsumerCategory) => {

                                this.nodes$.next(this.nodes$.getValue().concat(
                                    [
                                        {
                                            data: {},
                                            hasChildren: true,
                                            id: createdConsumerCategory.id,
                                            name: createdConsumerCategory.name

                                        }
                                    ]
                                ));
                                this.notificationService.success('Consumer Category Created');
                            },
                            error: (error) => {
                                this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                            }
                        });

                } else {
                    // User clicked cancel
                    this.notificationService.error('Cancelled Create Consumer Category');
                }
            });
    }

    openCreateConsumerDialog(node: TreeNode): void {
        const dialogRef = this.dialog.open(AddConsumerDialogComponent, {
            width: '600px',
            height: '475px',
            data: {categoryData: {id: node.data.id, name: node.data.name}}
        });
        dialogRef
            .afterClosed()
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe( {
                next: (consumerToCreate) => {
                    if (consumerToCreate !== undefined) {
                        this.startProgressBar();
                        this.managementApiService.createConsumer(consumerToCreate)
                            .pipe(
                                finalize(() => this.stopProgressBar()),
                                takeUntilDestroyed(this.destroyRef)
                            )
                            .subscribe((createdConsumer) => {
                                this.notificationService.success('Consumer Created');
                                const allNodes = this.nodes$.getValue();
                                const indexToModify = allNodes.findIndex(nd => nd.id === createdConsumer.consumerCategory);
                                const nodeToModify = allNodes[indexToModify];
                                // FIXME: console error when doing below push() in some scenarios
                                //  May only happen when creating a new consumer immediately after creating a new category
                                try {
                                nodeToModify['children'].push(createdConsumer);
                                allNodes.splice(indexToModify, 1, nodeToModify);
                                this.nodes$.next([].concat(allNodes));
                                this.getConsumerCategories();
                                this.getConsumersForCategory(createdConsumer.consumerCategory)
                                    .then(() => {
                                        this.stopProgressBar();
                                    });
                                } catch (e) {}
                            });
                    } else {
                        // User clicked cancel
                        this.notificationService.error('Cancelled Create Consumer');
                    }
                },
                error: () => {
                    this.notificationService.error('Failed to create consumer. Please try again.');
                }
            });
    }

    openDeleteConsumerDialog(node: TreeNode): void {
        const dialogRef = this.dialog.open(DeleteConsumerDialogComponent, {
            width: '600px',
            data: {categoryData: {id: node.data.id, name: node.data.name, tag: node.data.tag}}
        });
        try {
            dialogRef
                .afterClosed()
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe(result => {
                    if (result !== undefined) {
                        this.startProgressBar();
                        this.managementApiService.deleteConsumer(result.tag)
                            .pipe(
                                finalize(() => this.stopProgressBar()),
                                takeUntilDestroyed(this.destroyRef)
                            )
                            .subscribe({
                                next: () => {
                                    this.notificationService.success(`${node.data.name} Deleted`);
                                    this.getConsumerCategories();
                                    this.getConsumersForCategory(result?.source?.data?.categoryData?.tag).then(() => {
                                        this.stopProgressBar()
                                    });
                                },
                                error: (error) => {
                                    this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                                }
                            });
                    } else {
                        // User clicked cancel
                        this.notificationService.error('Cancelled Delete Consumer');
                    }
                });
        } catch (e) {
            this.notificationService.error(`Failed to delete ${node.data.name}`);
        }
    }

    openDuplicateConsumerDialog(node: TreeNode): void {
        const dialogRef = this.dialog.open(DuplicateConsumerDialogComponent, {
            width: '600px',
            height: '475px',
            data: {}
        });
        try {
            dialogRef
                .afterClosed()
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe(consumerToCreateData => {
                    if (consumerToCreateData !== undefined) {
                        this.startProgressBar();

                        const consumerToCreate = {
                            name: consumerToCreateData.name,
                            tag: consumerToCreateData.tag,
                        } as Consumer;

                        this.managementApiService.duplicateConsumer(node.data.tag,
                            consumerToCreate.tag, consumerToCreate.name)
                            .pipe(
                                finalize(() => this.stopProgressBar()),
                                takeUntilDestroyed(this.destroyRef)
                            )
                            .subscribe({
                                next: (createdConsumer) => {
                                    const allNodes = this.nodes$.getValue();
                                    const indexToModify = allNodes.findIndex(nd => nd.id === createdConsumer.consumerCategory);
                                    const nodeToModify = allNodes[indexToModify];
                                    try {
                                        nodeToModify['children'].push(createdConsumer);
                                        allNodes.splice(indexToModify, 1, nodeToModify);
                                        this.nodes$.next([].concat(allNodes));
                                        this.notificationService.success('Consumer Duplicated');
                                        this.getConsumerCategories();
                                        this.getConsumersForCategory(createdConsumer.consumerCategory).then(() => {
                                            this.stopProgressBar();
                                        });
                                    } catch (e) {}
                                },
                                error: (error) => {
                                    this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                                }
                            });
                    } else {
                        // User clicked cancel
                        this.notificationService.error('Cancelled Duplicate Consumer');
                    }
                });
        } catch (e) {
            this.notificationService.error(`Failed to duplicate ${node.data.name}`);
        }
    }

    duplicateConsumer(node: TreeNode): void {
        this.openDuplicateConsumerDialog(node);
    }

    onFilter(searchFilter: SearchFilter): void {
        this.filterNodes(searchFilter.value as string, searchFilter.key);
    }

    filterNodes(value: string, selected: string): void {

        this.nodes$.next(this.nodesCache);

        this.nodes$.pipe(
            take(1),
            map(fn => {
                const filteredNodes = fn.filter(nd => nd['name'].toLowerCase().includes(value.toLowerCase()));
                this.nodes$.next(filteredNodes);
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe();
    }

    selectConsumer(node: TreeNode) {
        if (node.isLeaf) {
            this.getConsumer(node.data.tag).then(() => {
                this.selectedNode = node;
                this.changeDetector.detectChanges();
                this.selectedNode$.next(node);
                node.setIsActive(true);
                this.changeDetector.detectChanges();
            });
        }
    }

    hasChanges(): boolean {
        return this.consumerManagerForm && this.consumerManagerForm.hasChanges();
    }

    cancel() {
        this.consumerManagerForm?.cancel();
    }

    submit() {
        this.consumerManagerForm?.submit();
    }

    toggleButtons() {
        this.disableButtons$.next(!this.disableButtons$.getValue());
    }
}
